import * as React from "react"
import { Link, useLocation } from "react-router-dom"
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuItem,
	SidebarMenuButton,
	SidebarMenuSub,
	SidebarMenuSubItem,
	SidebarMenuSubButton,
	SidebarGroup,
	SidebarGroupContent,
	SidebarTrigger,
	SidebarMobileTrigger,
	useSidebar,
} from "@shadcn/components/layouts/Sidebar/Sidebar"
import { SidebarInset } from "@shadcn/components/ui/sidebar"
import { Collapsible, CollapsibleContent } from "@shadcn/components/ui/collapsible"
import { cn } from "@shadcn/lib/utils"
import { AppHeader, type AppHeaderProps } from "./AppHeader"
import { AppLayout } from "../AppLayout"
import { NavigableCollapsibleTrigger } from "./NavigableCollapsibleTrigger"

export interface NavItem {
	label: string
	href: string
	icon?: React.ReactNode
	badge?: string | number
	children?: NavItem[]
}

export interface AppShellProps {
	children: React.ReactNode
	navItems: NavItem[]
	footerNavItems?: NavItem[]
	logo?: React.ReactNode
	userMenu?: React.ReactNode
	footer?: React.ReactNode
	defaultOpen?: boolean
	className?: string
	/**
	 * App header props (company, user, breadcrumbs)
	 */
	header?: AppHeaderProps
}

/**
 * Inner component that needs access to the Sidebar context
 */
function SidebarNavigation({
	navItems,
	footerNavItems,
	userMenu,
	footer,
}: {
	navItems: NavItem[]
	footerNavItems?: NavItem[]
	userMenu?: React.ReactNode
	footer?: React.ReactNode
}) {
	const location = useLocation()
	const { state } = useSidebar()

	// Track which collapsible items are open
	const [openItems, setOpenItems] = React.useState<Record<string, boolean>>(() => {
		// Initialize with items that have active children
		const initial: Record<string, boolean> = {}
		navItems.forEach((item) => {
			if (item.children && item.children.length > 0) {
				const anyChildActive = item.children.some(
					(child) => location.pathname === child.href || location.pathname.startsWith(`${child.href}/`)
				)
				initial[item.href] = anyChildActive
			}
		})
		return initial
	})

	// Save and restore collapsible state across sidebar collapse/expand
	const savedOpenItems = React.useRef<Record<string, boolean>>({})
	const prevState = React.useRef(state)

	React.useEffect(() => {
		if (prevState.current === state) return
		prevState.current = state

		if (state === "collapsed") {
			savedOpenItems.current = openItems
			setOpenItems({})
		} else {
			setOpenItems(savedOpenItems.current)
		}
	}, [state]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<SidebarHeader className="flex justify-between h-12 items-center border-b border-border  group-data-[state=collapsed]:justify-start text-muted-foreground">
				<h3 className="font-semibold text-sm  truncate whitespace-nowrap max-w-[195px] group-data-[state=collapsed]:hidden pl-2">
					Navigation
				</h3>
				<SidebarTrigger aria-label="Toggle sidebar" />
			</SidebarHeader>

			<SidebarContent className="group-data-[state=collapsed]:items-center">
				<SidebarGroup className="group-data-[state=collapsed]:items-center">
					<SidebarGroupContent className="group-data-[state=collapsed]:w-8">
						<SidebarMenu className="group-data-[state=collapsed]:w-full [&_span]:transition-all [&_span]:duration-200 [&_span]:ease-out [&_span]:opacity-100 [&_span]:translate-x-0 group-data-[state=collapsed]:[&_span]:opacity-0 group-data-[state=collapsed]:[&_span]:-translate-x-2 group-data-[state=collapsed]:[&_span]:pointer-events-none">
							{navItems.map((item) => {
								const isActive = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`)
								const hasChildren = item.children && item.children.length > 0

								// Regular menu item without children
								if (!hasChildren) {
									return (
										<SidebarMenuItem key={item.href}>
											<SidebarMenuButton asChild data-active={isActive} tooltip={item.label}>
												<Link to={item.href} className="flex items-center gap-3">
													{item.icon}
													<span>{item.label}</span>
												</Link>
											</SidebarMenuButton>
										</SidebarMenuItem>
									)
								}

								// Collapsible menu item with children
								const anyChildActive = item.children?.some(
									(child) => location.pathname === child.href || location.pathname.startsWith(`${child.href}/`)
								)

								return (
									<Collapsible
										key={item.href}
										open={openItems[item.href] ?? false}
										onOpenChange={(open) => {
											setOpenItems((prev) => ({ ...prev, [item.href]: open }))
										}}
										className="group/collapsible">
										<SidebarMenuItem>
											<NavigableCollapsibleTrigger
												href={item.href}
												isActive={anyChildActive ?? isActive}
												variant="default"
												size="default">
												{item.icon}
												<span className="group-data-[collapsible=icon]:hidden pl-1">{item.label}</span>
											</NavigableCollapsibleTrigger>
											<CollapsibleContent>
												<SidebarMenuSub>
													{item.children?.map((child) => {
														const childActive =
															location.pathname === child.href || location.pathname.startsWith(`${child.href}/`)
														return (
															<SidebarMenuSubItem key={child.href}>
																<SidebarMenuSubButton asChild data-active={childActive}>
																	<Link to={child.href}>
																		{child.icon}
																		<span>{child.label}</span>
																	</Link>
																</SidebarMenuSubButton>
															</SidebarMenuSubItem>
														)
													})}
												</SidebarMenuSub>
											</CollapsibleContent>
										</SidebarMenuItem>
									</Collapsible>
								)
							})}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			</SidebarContent>
			<SidebarFooter>
				{/* Footer Navigation Items (e.g., Settings) */}
				{footerNavItems && footerNavItems.length > 0 && (
					<div className="border-t border-border py-2">
						<SidebarMenu>
							{footerNavItems.map((item) => {
								const isActive = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`)
								const isExternalLink = item.href.startsWith("http://") || item.href.startsWith("https://")

								return (
									<SidebarMenuItem key={item.href}>
										<SidebarMenuButton asChild data-active={isActive} tooltip={item.label} className="text-foreground">
											{isExternalLink ? (
												<a href={item.href} target="_blank" rel="noopener noreferrer">
													{item.icon}
													<span>{item.label}</span>
												</a>
											) : (
												<Link to={item.href}>
													{item.icon}
													<span>{item.label}</span>
												</Link>
											)}
										</SidebarMenuButton>
									</SidebarMenuItem>
								)
							})}
						</SidebarMenu>
					</div>
				)}
				{userMenu && <div className="border-t border-border pt-2">{userMenu}</div>}
				{footer && <div className="border-t border-border py-2">{footer}</div>}
			</SidebarFooter>
		</>
	)
}

export function AppShell({
	children,
	navItems,
	footerNavItems,
	logo,
	userMenu,
	footer,
	defaultOpen = true,
	className,
	header,
}: Readonly<AppShellProps>) {
	return (
		<AppLayout
			header={header ? <AppHeader {...header} /> : undefined}
			sidebarVariant="docked"
			defaultSidebarOpen={defaultOpen}>
			{/* Sidebar with navigation */}
			<Sidebar collapsible="icon" className="relative h-full font-normal">
				<SidebarNavigation navItems={navItems} footerNavItems={footerNavItems} userMenu={userMenu} footer={footer} />
			</Sidebar>

			{/* Mobile sidebar trigger - only visible on narrow screens */}
			<SidebarMobileTrigger />

			{/* Main Content Area */}
			<SidebarInset>
				{/*
					  Scroll lock: when any descendant has .scroll-lock class,
					  main switches from overflow-y-auto to overflow-hidden.
					  Used by LockedLoadingDisplay and Modal to prevent background scrolling
					  during loading animations and preview overlays.
					*/}
				<main
					className={cn(
						"flex-1 overflow-y-auto relative bg-neutral-50 [&:has(.scroll-lock)]:overflow-hidden",
						className
					)}>
					{children}
				</main>
			</SidebarInset>
		</AppLayout>
	)
}

export default AppShell
